/**
 * TradingModeBadge — always-visible, fail-safe PAPER / LIVE indicator.
 *
 * Reads the authoritative backend mode from GET /api/trading/config, which
 * exposes both env gates: `paper_trading` (the canonical resolver result,
 * defaults to paper) and `mode` (raw TRADING_MODE). The badge must NEVER
 * render a false "LIVE" or a false "PAPER":
 *   - an errored / unconfirmable fetch shows "UNKNOWN" (never a guessed mode),
 *   - a live signal from EITHER field shows "LIVE" (the two gates are
 *     uncoupled, so we over-warn LIVE rather than risk a false PAPER).
 *
 * NOTE: this reflects the API container's environment. If the autonomous bot
 * (brain.py / trader.py) runs in a separate process/container, confirm its env
 * matches — the badge is only as authoritative as the API it reads.
 */
import { useQuery } from "@tanstack/react-query";

import { apiGet } from "@/lib/api-client";
import { cn } from "@/lib/cn";

export interface TradingConfigResponse {
  mode?: string;
  paper_trading?: boolean;
  dry_run?: boolean;
  /**
   * Canonical resolver verdict from the backend (trader.py's own
   * resolve_execution_mode policy): exactly "paper" or "live", or null when
   * the resolver itself failed. Older deployed APIs omit the field entirely.
   */
  execution_mode?: "paper" | "live" | string | null;
  /** resolve_is_paper() verdict (agent-pod gate); null = resolver failed. */
  is_paper?: boolean | null;
}

export type TradingModeDisplay = "LIVE" | "PAPER" | "UNKNOWN" | "LOADING";

interface ResolveArgs {
  data?: TradingConfigResponse | null;
  isError: boolean;
  isLoading: boolean;
}

export function resolveTradingModeDisplay({
  data,
  isError,
  isLoading,
}: ResolveArgs): TradingModeDisplay {
  // Fail-safe: an errored fetch is never trusted — not even with stale cached
  // data. We do not assert a mode we could not just confirm.
  if (isError) return "UNKNOWN";
  if (!data) return isLoading ? "LOADING" : "UNKNOWN";

  const normalizedMode = String(data.mode ?? "").trim().toUpperCase();
  const paper = data.paper_trading;
  const resolved = String(data.execution_mode ?? "").trim().toLowerCase();

  // Preferred signal: the backend's canonical resolver verdict
  // (resolve_execution_mode — the SAME fail-closed policy trader.py runs on
  // the money path). LIVE from the resolver is always trusted. A resolver
  // PAPER is trusted only when no raw gate disagrees: if raw TRADING_MODE or
  // paper_trading=false still says live, keep the over-warn LIVE below —
  // a divergent container env must never render a calming PAPER.
  if (resolved === "live") return "LIVE";
  if (resolved === "paper" && paper !== false && normalizedMode !== "LIVE") {
    return "PAPER";
  }

  // Over-warn LIVE if EITHER backend gate indicates live. The backend has TWO
  // mode resolvers that DIVERGE on stale legacy vars:
  //   - trader.py (the autonomous money path) uses resolve_execution_mode():
  //     live  <=>  TRADING_MODE=LIVE; it IGNORES PAPER_TRADING entirely.
  //   - resolve_is_paper() (behind /api/trading/config.paper_trading) fail-
  //     closes to paper when a stale legacy PAPER_* var says paper.
  // So TRADING_MODE=LIVE + stale PAPER_TRADING=true => trader.py trades LIVE
  // while paper_trading reads true. Trusting paper_trading alone would show a
  // FALSE PAPER on the bot's real money path. Keying LIVE off raw TRADING_MODE
  // (`mode`) too closes that hole. Direction of error is intentional: over-warn
  // LIVE, never a false PAPER.
  // Accepted over-warn: `mode` is RAW TRADING_MODE (not resolve_execution_mode),
  // so a TRADING_MODE=LIVE + EXECUTION_MODE=paper misconfig (trader.py forced to
  // paper) shows LIVE — a false-LIVE that usefully flags the misconfig and stays
  // on the safe side. (A fuller fix would expose resolve_execution_mode via the
  // API; tracked as a follow-up.)
  // Caveat: this reflects the API container's env; the trader is a separate
  // process — confirm both share TRADING_MODE at flip.
  if (paper === false || normalizedMode === "LIVE") return "LIVE";
  if (paper === true || normalizedMode === "PAPER") return "PAPER";
  // Data present but neither field carries a usable signal -> do not guess.
  return "UNKNOWN";
}

export function useTradingConfig() {
  return useQuery({
    queryKey: ["trading", "config"],
    queryFn: () => apiGet<TradingConfigResponse>("/api/trading/config"),
    refetchInterval: 15_000,
    staleTime: 10_000,
  });
}

const MODE_STYLES: Record<
  TradingModeDisplay,
  { label: string; className: string; title: string }
> = {
  LIVE: {
    label: "LIVE",
    className: "border-red-500/50 bg-red-500/15 text-red-300 animate-pulse",
    title: "Trading mode: LIVE — real money is at risk",
  },
  PAPER: {
    label: "PAPER",
    className: "border-blue-500/40 bg-blue-500/10 text-blue-300",
    title: "Trading mode: PAPER — simulated, no real money",
  },
  UNKNOWN: {
    label: "UNKNOWN",
    className: "border-amber-500/50 bg-amber-500/10 text-amber-300",
    title: "Trading mode: UNKNOWN — backend unreachable, mode unconfirmed",
  },
  LOADING: {
    label: "…",
    className: "border-border bg-transparent text-muted",
    title: "Trading mode: loading…",
  },
};

export function TradingModeBadge({ className }: { className?: string }) {
  const { data, isError, isLoading } = useTradingConfig();
  const display = resolveTradingModeDisplay({ data, isError, isLoading });
  const style = MODE_STYLES[display];

  return (
    <span
      role="status"
      aria-label={style.title}
      title={style.title}
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold tracking-wide",
        style.className,
        className,
      )}
    >
      {style.label}
    </span>
  );
}
